import React, {useCallback, useEffect, useRef, useState} from "react";
import {ColumnFilterProps} from "./types";
import {countries} from "@/lib/countries";
import {APIProvider, Map, AdvancedMarker, MapMouseEvent} from "@vis.gl/react-google-maps";
import {Circle} from "@/features/operational-map-v2/circle";
import {ButtonV2} from "@/components/ui/ButtonsV2/ButtonV2";
import {useDropdownPosition} from "@/hooks/useDropdownPosition";
import {SearchInput} from "@/components/search-input";

type Country = {
    countryCode: string;
    countryNameEn: string;
    flag: string;
};

type FilterMode = "country" | "location";

export const CountryLocationFilter: React.FC<ColumnFilterProps> = ({label, onChange, filterParams}) => {
    const googleMapsApiKey: string = process.env.REACT_APP_GOOGLE_MAP_API_KEY!;
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const position = useDropdownPosition(isOpen, dropdownRef, 700);

    // Get initial mode from filter params
    const getInitialMode = useCallback((): FilterMode => {
        if (filterParams.filterMode === "country") return "country";
        if (filterParams.filterMode === "location") return "location";
        // Default based on what's set
        if (filterParams.countryISO) return "country";
        if (filterParams.latitude !== undefined) return "location";
        return "country";
    }, [filterParams]);

    // Applied state (what's in the active filter)
    const [appliedMode, setAppliedMode] = useState<FilterMode>(getInitialMode);
    const [appliedCountry, setAppliedCountry] = useState<Country | null>(
        filterParams.countryISO && filterParams.countryName
            ? {
                  countryCode: filterParams.countryISO as string,
                  countryNameEn: filterParams.countryName as string,
                  flag: (filterParams.countryFlag as string) || "",
              }
            : null
    );
    const [appliedLatitude, setAppliedLatitude] = useState<number | undefined>(
        typeof filterParams.latitude === "number" ? filterParams.latitude : undefined
    );
    const [appliedLongitude, setAppliedLongitude] = useState<number | undefined>(
        typeof filterParams.longitude === "number" ? filterParams.longitude : undefined
    );
    const [appliedRadius, setAppliedRadius] = useState<number>(
        typeof filterParams.locationRadius === "number" ? filterParams.locationRadius : 50000
    );

    // Staged state (what's being edited in the dropdown)
    const [filterMode, setFilterMode] = useState<FilterMode>(getInitialMode);
    const [selectedCountry, setSelectedCountry] = useState<Country | null>(
        filterParams.countryISO && filterParams.countryName
            ? {
                  countryCode: filterParams.countryISO as string,
                  countryNameEn: filterParams.countryName as string,
                  flag: (filterParams.countryFlag as string) || "",
              }
            : null
    );
    const [latitude, setLatitude] = useState<number | undefined>(
        typeof filterParams.latitude === "number" ? filterParams.latitude : undefined
    );
    const [longitude, setLongitude] = useState<number | undefined>(
        typeof filterParams.longitude === "number" ? filterParams.longitude : undefined
    );
    const [radius, setRadius] = useState<number>(
        typeof filterParams.locationRadius === "number" ? filterParams.locationRadius : 50000
    ); // Default 50km in meters
    const [countrySearch, setCountrySearch] = useState("");

    const handleToggle = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isOpen) {
            // When opening, sync staged state with applied state
            setFilterMode(appliedMode);
            setSelectedCountry(appliedCountry);
            setLatitude(appliedLatitude);
            setLongitude(appliedLongitude);
            setRadius(appliedRadius);
        }
        setIsOpen(!isOpen);
    }, [isOpen, appliedMode, appliedCountry, appliedLatitude, appliedLongitude, appliedRadius]);

    const handleModeChange = useCallback((mode: FilterMode) => {
        setFilterMode(mode);
        // Don't clear selections - just switch the mode
        // The selections are preserved so user can switch back without losing data
    }, []);


    const handleMapClick = useCallback((e: MapMouseEvent) => {
        if (e.detail.latLng) {
            setLatitude(Number(e.detail.latLng.lat.toFixed(6)));
            setLongitude(Number(e.detail.latLng.lng.toFixed(6)));
        }
    }, []);

    const handleRadiusChanged = useCallback((newRadius: number) => {
        setRadius(Math.round(newRadius));
    }, []);

    const handleCenterChanged = useCallback((newCenter: google.maps.LatLng | null | undefined) => {
        if (newCenter) {
            setLatitude(Number(newCenter.lat().toFixed(6)));
            setLongitude(Number(newCenter.lng().toFixed(6)));
        }
    }, []);

    const handleApply = useCallback(() => {
        if (filterMode === "country") {
            if (selectedCountry) {
                // Save to applied state
                setAppliedMode("country");
                setAppliedCountry(selectedCountry);
                setAppliedLatitude(undefined);
                setAppliedLongitude(undefined);
                setAppliedRadius(50000);

                onChange({
                    filterMode: "country",
                    countryISO: selectedCountry.countryCode,
                    countryName: selectedCountry.countryNameEn,
                    countryFlag: selectedCountry.flag,
                    // Clear location-related keys
                    latitude: undefined,
                    longitude: undefined,
                    locationRadius: undefined,
                });
            } else {
                // Clear all
                setAppliedMode("country");
                setAppliedCountry(null);
                setAppliedLatitude(undefined);
                setAppliedLongitude(undefined);
                setAppliedRadius(50000);

                onChange({
                    filterMode: undefined,
                    countryISO: undefined,
                    countryName: undefined,
                    countryFlag: undefined,
                    latitude: undefined,
                    longitude: undefined,
                    locationRadius: undefined,
                });
            }
        } else {
            if (latitude !== undefined && longitude !== undefined && radius > 0) {
                // Save to applied state
                setAppliedMode("location");
                setAppliedCountry(null);
                setAppliedLatitude(latitude);
                setAppliedLongitude(longitude);
                setAppliedRadius(radius);

                onChange({
                    filterMode: "location",
                    latitude,
                    longitude,
                    locationRadius: radius,
                    // Clear country-related keys
                    countryISO: undefined,
                    countryName: undefined,
                    countryFlag: undefined,
                });
            } else {
                // Clear all
                setAppliedMode("location");
                setAppliedCountry(null);
                setAppliedLatitude(undefined);
                setAppliedLongitude(undefined);
                setAppliedRadius(50000);

                onChange({
                    filterMode: undefined,
                    countryISO: undefined,
                    countryName: undefined,
                    countryFlag: undefined,
                    latitude: undefined,
                    longitude: undefined,
                    locationRadius: undefined,
                });
            }
        }
        setIsOpen(false);
    }, [filterMode, selectedCountry, latitude, longitude, radius, onChange]);

    const handleClear = useCallback(() => {
        // Clear both staged and applied state
        setSelectedCountry(null);
        setLatitude(undefined);
        setLongitude(undefined);
        setRadius(50000);
        setAppliedCountry(null);
        setAppliedLatitude(undefined);
        setAppliedLongitude(undefined);
        setAppliedRadius(50000);

        onChange({
            filterMode: undefined,
            countryISO: undefined,
            countryName: undefined,
            countryFlag: undefined,
            latitude: undefined,
            longitude: undefined,
            locationRadius: undefined,
        });
    }, [onChange]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    // Update state when filterParams change (e.g., from URL)
    useEffect(() => {
        const mode = getInitialMode();
        setFilterMode(mode);
        setAppliedMode(mode);

        if (mode === "country" && filterParams.countryISO) {
            const country = countries.find(c => c.countryCode === filterParams.countryISO);
            if (country) {
                const countryObj = {
                    countryCode: country.countryCode,
                    countryNameEn: country.countryNameEn,
                    flag: country.flag,
                };
                setSelectedCountry(countryObj);
                setAppliedCountry(countryObj);
            }
        } else if (mode === "location") {
            if (typeof filterParams.latitude === "number") {
                setLatitude(filterParams.latitude);
                setAppliedLatitude(filterParams.latitude);
            }
            if (typeof filterParams.longitude === "number") {
                setLongitude(filterParams.longitude);
                setAppliedLongitude(filterParams.longitude);
            }
            if (typeof filterParams.locationRadius === "number") {
                setRadius(filterParams.locationRadius);
                setAppliedRadius(filterParams.locationRadius);
            }
        }
    }, [getInitialMode, filterParams]);

    const mapCenter = latitude !== undefined && longitude !== undefined
        ? { lat: latitude, lng: longitude }
        : { lat: 51.5074, lng: -0.1278 }; // Default to London

    // Use applied state for the label display
    const hasFilter = appliedMode === "country"
        ? !!appliedCountry
        : appliedLatitude !== undefined && appliedLongitude !== undefined;

    return (
        <div className="relative inline-block" ref={dropdownRef}>
            <button
                onClick={handleToggle}
                className="flex items-center gap-1 hover:text-blue-600 transition-colors font-bold"
            >
                <span>{label}</span>
                {hasFilter && (
                    <span className="text-xs text-blue-600 font-normal">
                        {appliedMode === "country"
                            ? `(${appliedCountry?.countryNameEn})`
                            : `(${(appliedRadius / 1000).toFixed(0)}km radius)`
                        }
                    </span>
                )}
                <span className={`text-xs transition-transform ${isOpen ? "rotate-180" : ""}`}>▼</span>
            </button>

            {isOpen && (
                <div className={`absolute top-full ${position === "left" ? "left-0" : "right-0"} mt-1 bg-white rounded shadow-lg z-50 w-[700px] flex flex-col`} onClick={(e) => e.stopPropagation()}>
                    {/* Mode Toggle */}
                    <div className="p-3 border-b">
                        <div className="flex gap-2">
                            <button
                                onClick={() => handleModeChange("country")}
                                className={`flex-1 px-4 py-2 rounded font-medium transition-colors ${
                                    filterMode === "country"
                                        ? "bg-blue text-white"
                                        : "bg-light-light-grey text-grey hover:bg-light-grey"
                                }`}
                            >
                                Filter by Country
                            </button>
                            <button
                                onClick={() => handleModeChange("location")}
                                className={`flex-1 px-4 py-2 rounded font-medium transition-colors ${
                                    filterMode === "location"
                                        ? "bg-blue text-white"
                                        : "bg-light-light-grey text-grey hover:bg-light-grey"
                                }`}
                            >
                                Filter by Location
                            </button>
                        </div>
                    </div>

                    {/* Content Area */}
                    <div className="flex-1 overflow-auto p-3">
                        {filterMode === "country" ? (
                            <div className="flex flex-col gap-2">
                                <SearchInput
                                    initialValue={countrySearch}
                                    onChange={setCountrySearch}
                                />
                                <div className="mt-2 max-h-[300px] overflow-y-auto">
                                    {countries
                                        .filter(country =>
                                            country.countryNameEn.toLowerCase().includes(countrySearch.toLowerCase())
                                        )
                                        .map((country) => (
                                            <button
                                                key={country.countryCode}
                                                onClick={() => setSelectedCountry(country)}
                                                className={`w-full text-left px-3 py-2 hover:bg-light-blue transition-colors flex items-center gap-2 ${
                                                    selectedCountry?.countryCode === country.countryCode ? "bg-light-blue" : ""
                                                }`}
                                            >
                                                <span className="text-xl">{country.flag}</span>
                                                <span>{country.countryNameEn}</span>
                                            </button>
                                        ))}
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-4">
                                <div>
                                    <label className="font-medium block mb-2">Select Location and Radius</label>
                                    <p className="text-sm text-grey mb-2">Click on the map to select coordinates</p>
                                </div>

                                <div className="flex flex-col gap-2">
                                    <label className="font-medium">Radius (km)</label>
                                    <input
                                        type="number"
                                        value={radius / 1000}
                                        onChange={(e) => setRadius(Math.max(1, parseFloat(e.target.value) || 1) * 1000)}
                                        className="border border-gray-300 rounded px-3 py-2"
                                        min="1"
                                        step="1"
                                    />
                                </div>

                                <div className="w-full h-64 rounded overflow-hidden">
                                    <APIProvider apiKey={googleMapsApiKey}>
                                        <Map
                                            mapId={process.env.REACT_APP_GOOGLE_MAP_ID}
                                            defaultCenter={mapCenter}
                                            defaultZoom={latitude !== undefined ? 8 : 4}
                                            onClick={handleMapClick}
                                            gestureHandling="greedy"
                                            disableDefaultUI={false}
                                            zoomControl={true}
                                            fullscreenControl={false}
                                            streetViewControl={false}
                                            mapTypeControl={true}
                                            style={{width: "100%", height: "100%"}}
                                        >
                                            {latitude !== undefined && longitude !== undefined && (
                                                <>
                                                    <AdvancedMarker position={mapCenter}>
                                                        <div
                                                            className="w-8 h-8 bg-red-500 rounded-full border-4 border-white shadow-lg flex items-center justify-center"
                                                            style={{transform: "translate(0, 50%)"}}
                                                        >
                                                            <div className="w-2 h-2 bg-white rounded-full"></div>
                                                        </div>
                                                    </AdvancedMarker>
                                                    <Circle
                                                        center={mapCenter}
                                                        radius={radius}
                                                        strokeColor="#ef4444"
                                                        strokeOpacity={0.8}
                                                        strokeWeight={2}
                                                        fillColor="#f87171"
                                                        fillOpacity={0.2}
                                                        editable={true}
                                                        draggable={true}
                                                        onRadiusChanged={handleRadiusChanged}
                                                        onCenterChanged={handleCenterChanged}
                                                    />
                                                </>
                                            )}
                                        </Map>
                                    </APIProvider>
                                </div>

                                {latitude !== undefined && longitude !== undefined && (
                                    <div className="text-sm text-grey">
                                        <div>Latitude: {latitude.toFixed(6)}</div>
                                        <div>Longitude: {longitude.toFixed(6)}</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="p-3 flex gap-2">
                        <ButtonV2 label="Clear" color="gray" onClick={handleClear}/>
                        <ButtonV2 label="Apply Filter" color="blue" onClick={handleApply}/>
                    </div>
                </div>
            )}
        </div>
    );
};
